import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { FileDown, Search, CheckCircle, Clock, X, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const fmt = (v) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const S = {
  heading: { fontSize:22, fontWeight:700, color:'#f6c90e', marginBottom:4 },
  sub: { fontSize:13, color:'#475569', marginBottom:24 },
  toolbar: { display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' },
  searchBox: { display:'flex', alignItems:'center', gap:8, background:'#0e1420', border:'1px solid #1a2035', borderRadius:8, padding:'8px 12px', flex:1, maxWidth:300 },
  searchInput: { background:'none', border:'none', color:'#e2e8f0', fontSize:13, outline:'none', flex:1 },
  th: { padding:'10px 14px', textAlign:'left', fontSize:11, color:'#475569', textTransform:'uppercase', letterSpacing:1, borderBottom:'1px solid #1a2035' },
  td: { padding:'12px 14px', fontSize:13, color:'#cbd5e1', borderBottom:'1px solid #0f1117' },
  btn: (c='#f6c90e', disabled=false) => ({
    display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:7,
    background: disabled ? '#1a2035' : `${c}22`,
    color: disabled ? '#475569' : c,
    fontSize:12, fontWeight:600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    border:`1px solid ${disabled ? '#2d3748' : c + '44'}`,
    whiteSpace:'nowrap', textDecoration:'none',
  }),
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' },
  modal: { background:'#0e1420', border:'1px solid #1a2035', borderRadius:14, padding:28, width:600, maxWidth:'95vw', maxHeight:'85vh', display:'flex', flexDirection:'column' },
};

const linkStyle = { background:'none', border:'none', color:'#e2e8f0', fontWeight:700, cursor:'pointer', padding:0, fontSize:'inherit', textDecoration:'underline', textDecorationColor:'rgba(226,232,240,0.3)', textUnderlineOffset:2 };

/* ─── Modal de seleção de kit (até 3) + valor por kit ─── */
function KitSelectorModal({ lead, onConfirm, onClose, isChefe }) {
  const MAX_KITS = 3;
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [step, setStep] = useState(1); // 1=Kit Solar, 2=Valor

  const candidatosIniciais = (lead.kits_propostos && lead.kits_propostos.length > 0)
    ? lead.kits_propostos.map(c => ({ id: c.kit_id, equipamento: c.kit_equipamento, marca: c.kit_marca, geracao: c.kit_geracao, valor: c.kit_valor, placas_w: c.kit_placas_w, quantidade: c.kit_quantidade, qtd_inversores: c.kit_qtd_inversores }))
    : (lead.kit_id ? [{ id: lead.kit_id, equipamento: lead.kit_equipamento, marca: lead.kit_marca, geracao: lead.kit_geracao, valor: lead.kit_valor, placas_w: lead.kit_placas_w, quantidade: lead.kit_quantidade, qtd_inversores: lead.kit_qtd_inversores }] : []);
  const valoresIniciais = {};
  (lead.kits_propostos || []).forEach(c => {
    if (c.valor_proposto) valoresIniciais[c.kit_id] = String(Number(c.valor_proposto).toFixed(2)).replace('.', ',');
  });
  if (Object.keys(valoresIniciais).length === 0 && lead.valor_total_venda && lead.kit_id) {
    valoresIniciais[lead.kit_id] = String(Number(lead.valor_total_venda).toFixed(2)).replace('.', ',');
  }

  const [selectedKits, setSelectedKits] = useState(candidatosIniciais);
  const [valoresPorKit, setValoresPorKit] = useState(valoresIniciais);

  useEffect(() => {
    api.get('/kits/').then(r => { setKits(r.data); setLoading(false); });
  }, []);

  useEffect(() => {
    setValoresPorKit(prev => {
      const next = { ...prev };
      selectedKits.forEach(k => {
        if (!next[k.id] && k.valor) next[k.id] = Number(k.valor).toFixed(2).replace('.', ',');
      });
      return next;
    });
  }, [selectedKits]);

  const valorNumFor = (kitId) => {
    const s = valoresPorKit[kitId] || '';
    return parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0;
  };

  const toggleKit = (k) => {
    setSelectedKits(prev => {
      const exists = prev.find(p => p.id === k.id);
      if (exists) return prev.filter(p => p.id !== k.id);
      if (prev.length >= MAX_KITS) { alert(`Você pode selecionar no máximo ${MAX_KITS} kits.`); return prev; }
      return [...prev, k];
    });
  };

  const filtered = search.trim()
    ? kits.filter(k =>
        k.equipamento.toLowerCase().includes(search.toLowerCase()) ||
        (k.marca || '').toLowerCase().includes(search.toLowerCase()) ||
        String(k.geracao).includes(search)
      )
    : kits;

  const step1Ok = selectedKits.length >= 1 && selectedKits.length <= MAX_KITS;
  const step2Ok = selectedKits.every(k => valorNumFor(k.id) > 0);
  const comissaoPct = lead.vendedor_comissao_pct || 0;

  const handleConfirmar = () => {
    const kitsPayload = selectedKits.map(k => ({ kit_id: k.id, valor: valorNumFor(k.id) }));
    onConfirm(kitsPayload);
  };

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div>
            <div style={{ fontSize:16, fontWeight:700, color:'#f6c90e' }}>{step === 1 ? 'Kit Solar' : 'Valor'}</div>
            <div style={{ fontSize:12, color:'#475569', marginTop:2 }}>Cliente: {lead.cliente}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer' }}><X size={18}/></button>
        </div>

        {step === 1 && (
          <>
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:10 }}>Escolha de 1 a {MAX_KITS} kits solares para esta proposta:</div>
            {selectedKits.length > 0 && (
              <div style={{ background:'rgba(74,222,128,0.08)', border:'1px solid rgba(74,222,128,0.3)', borderRadius:8,
                            padding:'9px 14px', marginBottom:10, fontSize:13, color:'#4ade80', fontWeight:600 }}>
                ✅ {selectedKits.length} {selectedKits.length === 1 ? 'kit selecionado' : 'kits selecionados'}
              </div>
            )}
            <div style={{ ...S.searchBox, maxWidth:'100%', marginBottom:12 }}>
              <Search size={14} color="#475569"/>
              <input
                style={S.searchInput}
                placeholder="Buscar por equipamento ou geração..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
              />
            </div>

            <div style={{ flex:1, overflowY:'auto', overflowX:'auto', marginBottom:16, maxHeight:340 }}>
              {loading ? (
                <div style={{ textAlign:'center', padding:32, color:'#475569' }}>Carregando kits...</div>
              ) : filtered.length === 0 ? (
                <div style={{ textAlign:'center', padding:32, color:'#475569' }}>Nenhum kit encontrado</div>
              ) : (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:'#0b111e' }}>
                      <th style={S.th}>Geração</th>
                      <th style={S.th}>Placas</th>
                      <th style={S.th}>Qtd</th>
                      <th style={S.th}>Qtd Inversores</th>
                      <th style={S.th}>Equipamento</th>
                      <th style={S.th}>Marca</th>
                      <th style={{ ...S.th, textAlign:'right' }}>Valor</th>
                      <th style={S.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(k => {
                      const idx = selectedKits.findIndex(p => p.id === k.id);
                      const sel = idx !== -1;
                      return (
                        <tr
                          key={k.id}
                          onClick={() => toggleKit(k)}
                          style={{ cursor:'pointer', background: sel ? 'rgba(246,201,14,0.08)' : '', transition:'background 0.1s' }}
                          onMouseEnter={e => { if (!sel) e.currentTarget.style.background = '#111827'; }}
                          onMouseLeave={e => { if (!sel) e.currentTarget.style.background = ''; }}
                        >
                          <td style={S.td}>
                            <span style={{ background:'rgba(246,201,14,0.12)', color:'#f6c90e', border:'1px solid rgba(246,201,14,0.2)', borderRadius:20, padding:'2px 8px', fontSize:11, fontWeight:700 }}>
                              {k.geracao} kWh
                            </span>
                          </td>
                          <td style={S.td}>{k.placas_w} Wp</td>
                          <td style={S.td}>{k.quantidade}</td>
                          <td style={S.td}>{k.qtd_inversores ?? <span style={{ color:'#334155' }}>—</span>}</td>
                          <td style={{ ...S.td, fontWeight:600, color:'#e2e8f0' }}>
                            {sel && <span style={{ color:'#f6c90e', marginRight:6 }}>Opção {idx + 1}</span>}
                            {k.equipamento}
                          </td>
                          <td style={{ ...S.td, color:'#94a3b8' }}>{k.marca || <span style={{ color:'#334155' }}>—</span>}</td>
                          <td style={{ ...S.td, textAlign:'right', color:'#4ade80', fontWeight:700 }}>{fmt(k.valor)}</td>
                          <td style={S.td}>
                            <button onClick={e => { e.stopPropagation(); toggleKit(k); }}
                              style={{ background: sel ? 'transparent' : '#f6c90e', color: sel ? '#f87171' : '#080c14',
                                       border: sel ? '1px solid rgba(248,113,113,0.4)' : 'none', borderRadius:6, padding:'5px 12px',
                                       fontSize:11, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                              {sel ? 'Remover' : 'Escolher Este'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {step === 2 && (
          <div style={{ flex:1, overflowY:'auto', marginBottom:16 }}>
            {selectedKits.map((k, i) => {
              const kitValor = k.valor ? Number(k.valor) : 0;
              const valorNum = valorNumFor(k.id);
              const limiteDesconto = kitValor > 0 ? kitValor * 0.95 : 0;
              const temDesconto = !isChefe && kitValor > 0 && valorNum > 0 && valorNum < limiteDesconto;
              const pctDesconto = kitValor > 0 && valorNum > 0 ? ((1 - valorNum / kitValor) * 100) : 0;
              const comissaoVal = comissaoPct > 0 ? valorNum * comissaoPct / 100 : null;
              return (
                <div key={k.id} style={{ marginBottom: i < selectedKits.length - 1 ? 22 : 0, paddingBottom: i < selectedKits.length - 1 ? 18 : 0, borderBottom: i < selectedKits.length - 1 ? '1px solid #1a2035' : 'none' }}>
                  {selectedKits.length > 1 && (
                    <div style={{ fontSize:11, color:'#f6c90e', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Opção {i + 1} — {k.equipamento}</div>
                  )}
                  <div style={{ fontSize:12, color:'#64748b', marginBottom:6 }}>Qtd Inversores: <strong style={{ color:'#e2e8f0' }}>{k.qtd_inversores ?? '—'}</strong></div>
                  {kitValor > 0 && (
                    <div style={{ fontSize:12, color:'#64748b', marginBottom:8 }}>
                      💡 Valor do kit {selectedKits.length > 1 ? i + 1 : ''}: <strong style={{ color:'#e2e8f0' }}>{fmt(kitValor)}</strong>
                      {!isChefe && <span style={{ color:'#94a3b8' }}> · Baixando abaixo de 5%, a proposta e comissão serão revisadas pelo chefe</span>}
                    </div>
                  )}
                  <label style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:6, display:'block' }}>Valor total da venda (R$)</label>
                  <input
                    style={{ background:'#080c14', border:'1px solid #1a2035', borderRadius:8, padding:'9px 13px', outline:'none', width:'100%', boxSizing:'border-box',
                             fontSize:20, fontWeight:700, color: temDesconto ? '#f87171' : '#f6c90e', marginBottom:12 }}
                    type="text" placeholder="Ex: 37.555,00"
                    value={valoresPorKit[k.id] || ''}
                    onChange={e => setValoresPorKit(v => ({ ...v, [k.id]: e.target.value.replace(/[^0-9,]/g, '') }))}
                    autoFocus={i === 0}
                  />
                  {temDesconto ? (
                    <div style={{ background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.35)', borderRadius:10, padding:'12px 16px', marginBottom:12 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'#f87171', marginBottom:4 }}>⚠️ Desconto acima de 5%</div>
                      <div style={{ fontSize:13, color:'#fca5a5' }}>
                        O valor é <strong>{pctDesconto.toFixed(2)}%</strong> menor que o valor do kit ({fmt(kitValor)}).
                      </div>
                      <div style={{ fontSize:12, color:'#94a3b8', marginTop:6 }}>
                        O PDF da proposta será gerado, mas o chefe revisará a proposta e a comissão dessa venda.
                      </div>
                    </div>
                  ) : (
                    comissaoVal !== null && valorNum > 0 && comissaoPct > 0 && !isChefe && (
                      <div style={{ background:'rgba(74,222,128,0.07)', border:'1px solid rgba(74,222,128,0.2)', borderRadius:10, padding:'12px 16px', marginBottom:12 }}>
                        <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:4 }}>Sua comissão estimada</div>
                        <div style={{ fontSize:20, fontWeight:800, color:'#4ade80' }}>{fmt(comissaoVal)}</div>
                        <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>{comissaoPct}% sobre {fmt(valorNum)}</div>
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Rodapé */}
        <div style={{ display:'flex', justifyContent:'space-between', gap:10, borderTop:'1px solid #1a2035', paddingTop:14 }}>
          <button onClick={step === 1 ? onClose : () => setStep(1)} style={{ background:'transparent', color:'#64748b', border:'1px solid #1a2035', borderRadius:8, padding:'8px 16px', fontSize:13, cursor:'pointer' }}>
            {step === 1 ? 'Cancelar' : '← Voltar'}
          </button>
          {step === 1 ? (
            <button
              disabled={!step1Ok}
              onClick={() => step1Ok && setStep(2)}
              style={{ background: step1Ok ? '#f6c90e' : '#1a2035', color: step1Ok ? '#080c14' : '#475569', border:'none', borderRadius:8, padding:'8px 18px', fontSize:13, fontWeight:700, cursor: step1Ok ? 'pointer' : 'not-allowed' }}
            >
              Próximo →
            </button>
          ) : (
            <button
              disabled={!step2Ok}
              onClick={() => step2Ok && handleConfirmar()}
              style={{ background: step2Ok ? '#f6c90e' : '#1a2035', color: step2Ok ? '#080c14' : '#475569', border:'none', borderRadius:8, padding:'8px 18px', fontSize:13, fontWeight:700, cursor: step2Ok ? 'pointer' : 'not-allowed' }}
            >
              <span style={{ display:'flex', alignItems:'center', gap:6 }}><FileDown size={14}/> Confirmar e Gerar</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GeradorProposta() {
  const navigate = useNavigate();
  const { isChefe } = useAuth();
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [gerandoId, setGerandoId] = useState(null);
  const [view, setView] = useState('disponivel');
  const [kitModal, setKitModal] = useState(null); // lead objeto
  const [erro, setErro] = useState('');

  const fetchLeads = (v) => {
    setErro('');
    api.get(`/documentos/leads-propostas/?view=${v || 'disponivel'}`).then(r => setLeads(r.data)).catch(() => setErro('Erro ao carregar dados. Tente atualizar a página.'));
  };
  useEffect(() => { fetchLeads('disponivel'); }, []);

  const switchView = (v) => { setView(v); setSearch(''); fetchLeads(v); };

  const filtered = leads.filter(l =>
    l.cliente?.toLowerCase().includes(search.toLowerCase()) ||
    l.cidade?.toLowerCase().includes(search.toLowerCase())
  );

  const handleGerarClick = (lead) => {
    // Sempre abre modal de seleção de kit (inclusive para regerar)
    setKitModal(lead);
  };

  const handleKitConfirm = async (kitsPayload) => {
    const lead = kitModal;
    setKitModal(null);
    setGerandoId(lead.lead_id);
    try {
      // Gera proposta com até 3 kits candidatos; lead.kit só é definido na venda fechada
      const resp = await api.post(`/documentos/gerar-proposta/${lead.lead_id}/`, { kits: kitsPayload }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `proposta_${lead.lead_id}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      fetchLeads(view);
    } catch (e) {
      alert('Erro ao gerar proposta: ' + (e.response?.data?.error || e.message || 'tente novamente'));
    } finally {
      setGerandoId(null);
    }
  };

  return (
    <div>
      <div style={S.heading}>Gerador de Proposta</div>
      <div style={S.sub}>Gere propostas comerciais em PDF para seus clientes</div>
      {erro && (
        <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:13, marginBottom:14 }}>{erro}</div>
      )}
      <div style={S.toolbar}>
        <div style={S.searchBox}>
          <Search size={14} color="#475569" />
          <input
            style={S.searchInput}
            placeholder="Buscar cliente..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div style={{ display:'flex', gap:6 }}>
          {[
            { v:'disponivel', l:'Disponível' },
            { v:'fechadas',   l:'Vendas Fechadas' },
          ].map(opt => (
            <button
              key={opt.v}
              onClick={() => switchView(opt.v)}
              style={{
                padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:600,
                cursor:'pointer', border:'1px solid',
                background: view === opt.v ? 'rgba(246,201,14,0.15)' : 'transparent',
                color:       view === opt.v ? '#f6c90e' : '#64748b',
                borderColor: view === opt.v ? 'rgba(246,201,14,0.4)' : '#1a2035',
              }}
            >{opt.l}</button>
          ))}
        </div>
      </div>

      <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, overflowX:'auto', overflowY:'auto', maxHeight:400 }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              <th style={S.th}>#</th>
              <th style={S.th}>Cliente</th>
              <th style={S.th}>Cidade</th>
              <th style={S.th}>Vendedor</th>
              <th style={S.th}>Valor da Venda</th>
              <th style={S.th}>Kit</th>
              <th style={S.th}>{view === 'fechadas' ? 'Data da Venda' : 'Proposta'}</th>
              <th style={S.th}>Ação</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(l => {
              const loading = gerandoId === l.lead_id;
              return (
                <tr
                  key={l.lead_id}
                  onMouseEnter={e => e.currentTarget.style.background = '#111827'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={S.td}>#{l.lead_id}</td>
                  <td style={S.td}><button style={linkStyle} onClick={() => navigate(`/jornada/${l.cliente_id}`)}>{l.cliente}</button></td>
                  <td style={S.td}>{l.cidade}</td>
                  <td style={S.td}>{l.vendedor}</td>
                  <td style={S.td}>
                    {l.valor_total_venda != null
                      ? <span style={{ color:'#f6c90e', fontWeight:600 }}>
                          R$ {Number(l.valor_total_venda).toLocaleString('pt-BR', { minimumFractionDigits:2 })}
                        </span>
                      : <span style={{ color:'#475569' }}>—</span>
                    }
                  </td>
                  <td style={S.td}>
                    {view === 'fechadas' ? (
                      l.kit_geracao ? (
                        <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <span style={{ background:'rgba(246,201,14,0.12)', color:'#f6c90e', border:'1px solid rgba(246,201,14,0.2)', borderRadius:20, padding:'2px 8px', fontSize:11, fontWeight:700 }}>
                            {l.kit_geracao} kWh
                          </span>
                          <span style={{ fontSize:11, color:'#64748b' }}>{l.kit_equipamento}</span>
                        </span>
                      ) : (
                        <span style={{ color:'#475569', fontSize:12 }}>—</span>
                      )
                    ) : (l.kits_propostos && l.kits_propostos.length > 0) ? (
                      <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <Package size={13} color="#f6c90e" />
                        <span style={{ fontSize:12, color:'#94a3b8' }}>
                          {l.kits_propostos.length} {l.kits_propostos.length === 1 ? 'kit proposto' : 'kits propostos'}
                        </span>
                      </span>
                    ) : (
                      <span style={{ color:'#475569', fontSize:12 }}>—</span>
                    )}
                  </td>
                  <td style={S.td}>
                    {view === 'fechadas' ? (
                      <span style={{ color:'#4ade80', fontWeight:600, fontSize:13 }}>
                        {l.venda_fechada_em || '—'}
                      </span>
                    ) : l.proposta_gerada ? (
                      <a
                        href={l.proposta_pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display:'flex', alignItems:'center', gap:5, color:'#4ade80', fontSize:12, textDecoration:'none' }}
                      >
                        <CheckCircle size={13} /> Tem proposta
                      </a>
                    ) : (
                      <span style={{ display:'flex', alignItems:'center', gap:5, color:'#475569', fontSize:12 }}>
                        <Clock size={13} /> Sem proposta
                      </span>
                    )}
                  </td>
                  <td style={S.td}>
                    {view === 'fechadas' ? (
                      l.proposta_pdf_url
                        ? <a href={l.proposta_pdf_url} target="_blank" rel="noopener noreferrer" style={S.btn('#4ade80')}>
                            <FileDown size={13} /> Ver Proposta
                          </a>
                        : <span style={{ fontSize:12, color:'#475569' }}>Sem proposta gerada</span>
                    ) : (
                      <button
                        style={S.btn('#f6c90e', loading)}
                        onClick={() => !loading && handleGerarClick(l)}
                        disabled={loading}
                      >
                        <FileDown size={13} />
                        {loading ? 'Gerando...' : l.proposta_gerada ? 'Regerar' : 'Gerar Proposta'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} style={{ ...S.td, textAlign:'center', color:'#475569', padding:32 }}>
                  Nenhum lead encontrado
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {kitModal && (
        <KitSelectorModal
          lead={kitModal}
          isChefe={isChefe()}
          onConfirm={handleKitConfirm}
          onClose={() => setKitModal(null)}
        />
      )}
    </div>
  );
}
